import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductDesignerConfig, PrintColorOption } from '@customedge/shared';
import { DesignPreviewCanvas } from './DesignPreviewCanvas';
import { ErrorBoundary } from './ErrorBoundary';

// Merchant branding strip, same values/params as DesignerHeader's brand mark
// (?logo=/?logoLink=, set by Magento's design-preview-modal.js from
// Helper/Config::getLogoUrl()/getLogoLink() — see that file's own comment).
// This route has no header/tabs of its own to piggyback on (unlike the full
// designer, which goes through useDesignerInit), so read the params directly
// here instead. Falls back to nothing (not even "CustomEdge" text) when no
// logo is configured — unlike the edit popup, this read-only embed had no
// branding strip at all before, so an empty strip should collapse away
// entirely rather than show placeholder text no one asked for here.
function useWidgetBranding(): { logoUrl: string | null; logoLink: string | null } {
  const params = new URLSearchParams(window.location.search);
  return {
    logoUrl:  params.get('logo'),
    logoLink: params.get('logoLink'),
  };
}

// `&compact=1` — the PDP's small floating "keep your saved design in sight"
// popover embeds this same widget route at a tiny size; compact mode strips
// the branding strip (no room for it) and tells DesignPreviewCanvas to run
// its own compact/auto-slide behavior (see that component's own doc comment).
function useWidgetCompact(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get('compact') === '1';
}

function BrandingStrip({ logoUrl, logoLink }: { logoUrl: string | null; logoLink: string | null }) {
  const [failed, setFailed] = useState(false);
  if (!logoUrl || failed) return null;

  const img = (
    <img
      src={logoUrl}
      alt="Store logo"
      onError={() => setFailed(true)}
      style={{ maxHeight: 24, maxWidth: 140, objectFit: 'contain' }}
    />
  );

  return (
    <div style={{
      height: 36, flexShrink: 0, display: 'flex', alignItems: 'center',
      padding: '0 12px', background: '#ffffff', borderBottom: '1px solid #e2e8f0',
    }}>
      {logoLink ? <a href={logoLink} style={{ display: 'flex', alignItems: 'center' }}>{img}</a> : img}
    </div>
  );
}

// Public, embeddable 2D/3D preview for `?mode=widget&design={id}` — meant to
// fill a host page's own iframe slot (Magento cart/order pages, the CustomEdge
// admin Orders view), the same way Model3DPreviewRoot does for `?mode=3d-preview`
// but with both 2D and 3D, keyed by design id instead of SKU.
//
// Full-bleed (fills whatever size the embedding iframe was given), unlike
// ApprovalPreview's fixed 420px card — this is the whole page here, not a
// section within a larger approval-review layout.
export function DesignWidgetRoot({ designId }: { designId: string }) {
  const { t } = useTranslation();
  const { logoUrl, logoLink } = useWidgetBranding();
  const compact = useWidgetCompact();
  const [data, setData] = useState<{
    config: ProductDesignerConfig;
    jsonObjects: Record<string, any>;
    selectedVariantOptions?: Record<string, string>;
    colorVariantId?: string | null;
    printMethod?: string | null;
    printPositions?: string[];
    printMethodByPosition?: Record<string, string>;
    printColorByPosition?: Record<string, PrintColorOption>;
  } | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const widgetRes = await fetch(`/api/designs/${encodeURIComponent(designId)}/widget`);
        if (!widgetRes.ok) throw new Error(`HTTP ${widgetRes.status}`);
        const widget = await widgetRes.json();

        const configRes = await fetch(`/api/products/${encodeURIComponent(widget.productSku)}/config`);
        if (!configRes.ok) throw new Error(`HTTP ${configRes.status}`);
        const config = await configRes.json();

        if (!cancelled) {
          setData({
            config,
            jsonObjects: widget.jsonObjects,
            selectedVariantOptions: widget.selectedVariantOptions,
            colorVariantId: widget.colorVariantId,
            printMethod: widget.printMethod,
            printPositions: widget.printPositions,
            printMethodByPosition: widget.printMethodByPosition,
            printColorByPosition: widget.printColorByPosition,
          });
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [designId]);

  if (failed) {
    return <div style={fallbackStyle}>{t('approval.previewUnavailable')}</div>;
  }
  if (!data) {
    return <div style={fallbackStyle}>{t('approval.previewLoading')}</div>;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<div style={fallbackStyle}>{t('approval.previewLoading')}</div>}>
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
          {!compact && <BrandingStrip logoUrl={logoUrl} logoLink={logoLink} />}
          <div style={{ flex: 1, minHeight: 0 }}>
            <DesignPreviewCanvas
              designId={designId}
              config={data.config}
              jsonObjects={data.jsonObjects}
              selectedVariantOptions={data.selectedVariantOptions}
              colorVariantId={data.colorVariantId}
              printMethod={data.printMethod}
              printPositions={data.printPositions}
              printMethodByPosition={data.printMethodByPosition}
              printColorByPosition={data.printColorByPosition}
              compact={compact}
            />
          </div>
        </div>
      </Suspense>
    </ErrorBoundary>
  );
}

const fallbackStyle: React.CSSProperties = {
  height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: '#94a3b8', fontSize: 13,
};
